/* Album — ảnh lớp do mentor đăng; xem lớn trong hộp thoại, chuyển ảnh bằng phím ←/→. */
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Page, PageHeader, usePageTitle } from "@/components/page"
import { useClass } from "@/app/ClassLayout"
import { api } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { cn } from "@/lib/utils"

type Layout = "square" | "wide"

function LayoutSwitch({ value, onChange }: { value: Layout; onChange: (v: Layout) => void }) {
  const opts: { v: Layout; label: string; box: string }[] = [
    { v: "square", label: "Ảnh vuông 1:1", box: "size-4" },
    { v: "wide", label: "Ảnh rộng 16:9", box: "h-3 w-5" },
  ]
  return (
    <div role="group" aria-label="Chọn cách hiển thị album" className="flex gap-1 rounded-full border-[1.5px] border-edge bg-card p-1">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          title={o.label}
          aria-label={`Hiển thị ${o.label.toLowerCase()}`}
          aria-pressed={value === o.v}
          onClick={() => onChange(o.v)}
          className={cn("grid size-9 place-items-center rounded-full transition-colors", value === o.v ? "bg-orange text-white" : "text-ink hover:bg-soft")}
        >
          <span className={cn("rounded-[3px] border-2 border-current", o.box)} />
        </button>
      ))}
    </div>
  )
}

function PhotoCard({ photo, layout, onOpen }: { photo: any; layout: Layout; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Xem ảnh: ${photo.caption}`}
      className="group flex flex-col overflow-hidden rounded-[20px] border-[1.5px] border-edge bg-card text-left shadow-card transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card-hover"
    >
      <img
        src={photo.public_url}
        alt={photo.caption}
        loading="lazy"
        className={cn("w-full bg-soft object-cover", layout === "square" ? "aspect-square" : "aspect-video")}
      />
      <span className="flex flex-col gap-0.5 p-4">
        <strong className="line-clamp-2 font-semibold text-ink">{photo.caption}</strong>
        {photo.location && <span className="truncate text-sm text-muted-foreground">{photo.location}</span>}
        {photo.captured_at && <time className="text-sm text-muted-foreground">{formatDateTime(photo.captured_at)}</time>}
      </span>
    </button>
  )
}

function Lightbox({ photos, index, onIndex }: { photos: any[]; index: number | null; onIndex: (i: number | null) => void }) {
  const photo = index === null ? null : photos[index]
  const go = (d: number) => index !== null && onIndex((index + d + photos.length) % photos.length)
  useEffect(() => {
    if (index === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") onIndex((index + 1) % photos.length)
      if (e.key === "ArrowLeft") onIndex((index - 1 + photos.length) % photos.length)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [index, photos.length, onIndex])

  return (
    <Dialog open={!!photo} onOpenChange={(o) => !o && onIndex(null)}>
      <DialogContent className="max-h-[92svh] gap-0 overflow-y-auto p-0 sm:max-w-3xl">
        {photo && index !== null && (
          <>
            <div className="relative bg-ink">
              <img src={photo.public_url} alt={photo.caption} className="max-h-[60svh] w-full object-contain" />
              {photos.length > 1 && (
                <>
                  <Button size="icon" variant="outline" aria-label="Ảnh trước" onClick={() => go(-1)} className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full">
                    <Icon name="arrow-left" />
                  </Button>
                  <Button size="icon" variant="outline" aria-label="Ảnh tiếp theo" onClick={() => go(1)} className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full">
                    <Icon name="arrow-right" />
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-col gap-2 p-5">
              <span className="text-sm font-bold text-orange-strong">Khoảnh khắc {index + 1} / {photos.length}</span>
              <DialogTitle className="font-heading text-xl font-bold text-ink">{photo.caption}</DialogTitle>
              {photo.description && <DialogDescription className="text-base">{photo.description}</DialogDescription>}
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                {photo.location && (
                  <div><dt className="text-muted-foreground">Địa điểm</dt><dd className="font-semibold text-ink">{photo.location}</dd></div>
                )}
                {photo.captured_at && (
                  <div><dt className="text-muted-foreground">Ngày giờ</dt><dd className="font-semibold text-ink">{formatDateTime(photo.captured_at)}</dd></div>
                )}
              </dl>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function AlbumPage() {
  const { slug } = useClass()
  usePageTitle("Album lớp học", "Những khoảnh khắc vui vẻ của lớp được mentor lưu lại.")
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const [layout, setLayout] = useState<Layout>("square")
  const [active, setActive] = useState<number | null>(null)

  const load = () => {
    setError("")
    api(`/api/classes/${encodeURIComponent(slug)}/album`)
      .then(setData)
      .catch((e) => setError(e.message))
  }
  useEffect(() => { load() }, [slug])
  const photos: any[] = data?.photos || []

  return (
    <Page>
      <PageHeader eyebrow="Class memories" title="Album lớp học" description="Những khoảnh khắc vui vẻ của lớp được mentor lưu lại." />
      {error && <ErrorState message={error} onRetry={load} />}
      {!data && !error && <LoadingState label="Đang tải album…" />}
      {data && !photos.length && (
        <EmptyState mascot="giraffe-happy" title="Album chưa có ảnh" description="Khi mentor đăng ảnh của lớp, ảnh sẽ xuất hiện tại đây." />
      )}
      {photos.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-3">
            <strong className="font-heading text-lg text-ink">{photos.length} khoảnh khắc</strong>
            <LayoutSwitch value={layout} onChange={setLayout} />
          </div>
          <div className={cn("grid gap-4", layout === "square" ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-1 md:grid-cols-2")}>
            {photos.map((p, i) => (
              <PhotoCard key={p.id} photo={p} layout={layout} onOpen={() => setActive(i)} />
            ))}
          </div>
        </>
      )}
      <Lightbox photos={photos} index={active} onIndex={setActive} />
    </Page>
  )
}
